import {
  Button,
  Col,
  Container,
  Form,
  Row,
  useToaster
} from 'react-angle-dashboard-components'
import { Heading, SmallGrayHeading } from '../../style'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { IQuoteFormData } from '../../QuoteForm'
import PreviousPlanFormModal from './PreviousPlanFormModal'
import { useMemo, useState } from 'react'
import { IPreviousPlanForm } from './previousPlanSchema'
import PreviousPlanCard from './PreviousPlanCard'
import FooterNav from '../../FooterNav'
import SkipModal from './SkipModal'
import { LEVEL_FUNDED, ACA_FULLY_INSURED, FULLY_INSURED_NON_ACA, SELF_FUNDED } from '../../../../utils'
import {
  useCreatePastMedicalPlan,
  useDeletePastMedicalPlan,
  useUpdatePastMedicalPlan
} from '../../../../hooks/quote.hooks'
import {
  mapDTOToPastMedPlanForm,
  mapPastMedPlanFormToDTO
} from '../../../../services/quote/mappers'

const coverageTypes = [
  {
    label: 'No Current Coverage',
    value: 'No Current Coverage'
  },
  {
    label: 'Level Funded',
    value: LEVEL_FUNDED
  },
  {
    label: 'ACA Fully Insured',
    value: ACA_FULLY_INSURED
  },
  {
    label: 'Fully Insured (Non-ACA)',
    value: FULLY_INSURED_NON_ACA
  },
  {
    label: 'Self funded',
    value: SELF_FUNDED
  },
]

let continueCallback = () => {}

const QuoteDetailsStep1 = () => {
  const { showError } = useToaster()
  const { control, formState, watch, trigger, setValue, register } =
    useFormContext<IQuoteFormData>()

  const quoteId = watch('entryId')?.toString()

  const { append: appendPlan, remove: removePlan } = useFieldArray({
    control,
    name: 'quoteDetails.step1.plans'
  })

  const [isSkipModalOpen, setIsSkipModalOpen] = useState(false)
  const [isPreviousPlanModalOpen, setIsPreviousPlanModalOpen] = useState(false)
  const [planToEdit, setPlanToEdit] = useState<IPreviousPlanForm | undefined>()

  const { mutateAsync: createPastMedPlan } = useCreatePastMedicalPlan()
  const { mutateAsync: updatePastMedPlan } = useUpdatePastMedicalPlan()
  const { mutateAsync: deletePastMedPlan } = useDeletePastMedicalPlan()

  const openModal = () => setIsPreviousPlanModalOpen(true)

  const closeModal = () => {
    setIsPreviousPlanModalOpen(false)
    setPlanToEdit(undefined)
  }

  const onAddPlan = async (plan: IPreviousPlanForm) => {
    if (quoteId) {
      try {
        if (planToEdit && plan.id) {
          await updatePastMedPlan({
            quoteId,
            entryId: plan.id.toString(),
            data: mapPastMedPlanFormToDTO(plan)
          })
          const index = plans.findIndex((p) => p.id === plan.id)
          if (index > -1) {
            setValue(`quoteDetails.step1.plans.${index}`, plan)
          }
        } else {
          const returnedPlan = await createPastMedPlan({
            quoteId,
            data: mapPastMedPlanFormToDTO(plan)
          })
          appendPlan(mapDTOToPastMedPlanForm(returnedPlan))
        }
        trigger('quoteDetails.step1.plans')
        closeModal()
      } catch (err) {
        console.error(err)
        showError('There was a problem saving your previous plan')
      }
    } else {
      // throw error missing quoteId
    }
  }

  const openEditModal = (plan: IPreviousPlanForm) => {
    setPlanToEdit(plan)
    setIsPreviousPlanModalOpen(true)
  }

  const onDeletePlan = async (index: number) => {
    try {
      const plan = watch('quoteDetails.step1.plans')[index]
      if (quoteId && plan?.id) {
        await deletePastMedPlan({
          quoteId,
          entryId: plan.id.toString()
        })
        removePlan(index)
      }
    } catch (err) {
      console.error(err)
      showError('There was an error deleting this plan')
    }
  }

  const currentlySelectedCoverage = watch(
    'quoteDetails.step1.currentCoverageType'
  )
  const plans = watch('quoteDetails.step1.plans')

  const isNoneSelected = useMemo(
    () => currentlySelectedCoverage === 'No Current Coverage',
    [currentlySelectedCoverage]
  )

  const errors = formState.errors?.quoteDetails?.step1

  const onContinue = (callback: () => void) => {
    if (plans.length === 0) {
      if (!isNoneSelected) {
        // show warning modal
        continueCallback = callback
        setIsSkipModalOpen(true)
        return
      }
    }
    callback()
  }

  return (
    <Container className="p-4">
      <SkipModal
        open={isSkipModalOpen}
        onClose={() => setIsSkipModalOpen(false)}
        onSkip={continueCallback}
      />
      <PreviousPlanFormModal
        key={JSON.stringify(isPreviousPlanModalOpen)}
        open={isPreviousPlanModalOpen}
        onCreate={onAddPlan}
        onClose={closeModal}
        formValues={planToEdit}
      />
      <Row className="d-flex justify-content-between align-items-center pb-5">
        <Col>
          <SmallGrayHeading>Quote Details (1/2)</SmallGrayHeading>
          <Heading className="pt-1">Add Previous Plan Information</Heading>
        </Col>
        {plans?.length > 0 && (
          <Button onClick={openModal}>
            <i
              className="icon icon-plus text-white fs-12 mr-2"
              style={{ height: '100%', width: '0.8rem' }}
            />
            <span>Add previous plan</span>
          </Button>
        )}
      </Row>
      <Row>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>What is the group's current coverage type?</Form.Label>
            <Form.Control
              as="select"
              {...register('quoteDetails.step1.currentCoverageType')}
              onChange={(e) => {
                const val = e.target.value
                if (val === '') {
                  setValue('quoteDetails.step1.plans', [])
                }
                register('quoteDetails.step1.currentCoverageType').onChange(e)
              }}
              isInvalid={!!errors?.currentCoverageType}
            >
              {coverageTypes.map(({ label, value }) => (
                <option key={label} value={value}>
                  {label}
                </option>
              ))}
            </Form.Control>

            <Form.Control.Feedback type="invalid">
              {errors?.currentCoverageType?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      {currentlySelectedCoverage !== 'No Current Coverage' && (
        <>
          {plans.length === 0 ? (
            <Row>
              <Col className="d-flex align-items-center flex-column">
                <Row>
                  <div
                    style={{
                      fontWeight: 500,
                      fontSize: 20,
                      color: '#343A40'
                    }}
                  >
                    Enhance Rate Accuracy: Add Previous Plans
                  </div>
                </Row>
                <Row className="mt-2">
                  <SmallGrayHeading
                    style={{
                      textAlign: 'center',
                      color: '#6C757D',
                      fontWeight: 400
                    }}
                  >
                    To improve the accuracy of your quotes, we highly recommend
                    providing your previous plan information.
                    <br />
                    If you prefer to skip this step, you can select "Continue"
                  </SmallGrayHeading>
                </Row>
                <Row>
                  <Button className="mt-5" onClick={openModal} onCreate>
                    <i
                      className="icon icon-plus text-white fs-12 mr-2"
                      style={{ height: '100%', width: '0.8rem' }}
                    />
                    <span>Add previous plan</span>
                  </Button>
                </Row>
              </Col>
            </Row>
          ) : (
            <Row>
              {plans?.map((plan, index) => (
                <Col className="col-auto my-3">
                  <PreviousPlanCard
                    plan={plan}
                    onEdit={() => openEditModal(plan)}
                    onDelete={() => onDeletePlan(index)}
                  />
                </Col>
              ))}
            </Row>
          )}
        </>
      )}
      <FooterNav triggerPath="quoteDetails.step1" onContinue={onContinue} />
    </Container>
  )
}

export default QuoteDetailsStep1
